import React, { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useMotionValue, useReducedMotion } from 'framer-motion';
import { RoboticEyeScene } from './RoboticEyeScene';
import { pinnedCenterY } from '../hooks/useRoboticEyeScene';
import { RobotChat } from './RobotChat';
import { DockChat } from './DockChat';
import { CinemaOverlay } from './CinemaOverlay';
import { FINALE_AGAIN, FINALE_START } from '../data/chatScript';
import type { BubbleLink, ChatPhase, ChatSignal, CinemaLink } from '../types/chat';
import type { Emotion, EmotionMode } from '../types/emotion';
import type { FaceSide } from '../utils/droneFace';
import type { ArmsMode } from '../utils/droneArms';
interface DroneCompanionProps {
  /** Hero: the drone floats here on load and simply scrolls away with it (no chat, no docking) */
  heroRef: RefObject<HTMLElement>;
  /** Spot under the showreel where the drone sneaks in, chats, and later docks from */
  slotRef: RefObject<HTMLElement>;
  /** Once this element is scrolled well up, the drone moves to the slot */
  triggerRef: RefObject<HTMLElement>;
  /** Second spot after the work: he jumps out of the corner into it and starts a different conversation */
  finaleSlotRef: RefObject<HTMLElement>;
  /** Footer spot: once the footer is half in view he jumps out of the corner into its middle */
  footerSlotRef: RefObject<HTMLElement>;
  modelUrl: string;
  emotion: EmotionMode;
  screenScale: number;
  faceSide: FaceSide;
  armsMode: ArmsMode;
}

/** Chat starts this long after he has settled into the section spot (ms) */
const CHAT_DELAY = 900;
/** In the finale he flies in from the corner first, so he waits a little longer before talking */
const FINALE_CHAT_DELAY = 1700;
/** Finale spot takes over once its middle has risen to this fraction of the screen height */
const FINALE_AT = 0.8;
/** Footer spot takes over once the footer's top edge has risen to this fraction of the screen */
const FOOTER_AT = 0.5;

/**
 * Pixel + his chat, in a page-wide layer.
 * Hero: floats, rides along with the page, stays quiet.
 * Past the "We build brands…" line: sneaks in from the top to his section spot, starts chatting 3s later.
 * Past that section: flies to the bottom-right corner as a small launcher.
 */
export function DroneCompanion({
  heroRef,
  slotRef,
  triggerRef,
  finaleSlotRef,
  footerSlotRef,
  modelUrl,
  emotion,
  screenScale,
  faceSide,
  armsMode
}: DroneCompanionProps) {
  const reduce = useReducedMotion();
  const [docked, setDocked] = useState(false);
  const [dockLanded, setDockLanded] = useState(false);
  const [, setBubblesVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [stage, setStage] = useState<'hero' | 'section' | 'finale' | 'footer'>('hero');
  const [chat, setChat] = useState<ChatSignal>({
    phase: 'idle',
    emotion: 'neutral',
    pulse: 0
  });

  // The element the 3D scene treats as home — read every frame, so switching it moves the drone
  const activeAnchor = useRef<HTMLElement | null>(null);
  useEffect(() => {
    activeAnchor.current = stage === 'footer' ? footerSlotRef.current : stage === 'finale' ? finaleSlotRef.current : stage === 'section' ? slotRef.current : heroRef.current;
  }, [stage, heroRef, slotRef, finaleSlotRef, footerSlotRef]);

  // Hero ↔ section spot: Pixel stays pinned where he sits in the hero until the section spot
  // scrolls up under him, then he rides along with it (and is released again on the way back up)
  useEffect(() => {
    const check = () => {
      const hero = heroRef.current;
      const slot = slotRef.current;
      if (!hero || !slot || !triggerRef.current) return;
      const hr = hero.getBoundingClientRect();
      const pinnedY = pinnedCenterY(hr, window.innerHeight);
      const sr = slot.getBoundingClientRect();
      const slotY = sr.top + sr.height / 2;
      // After the work: once the finale spot is on screen he leaves the corner for it
      // (switching home while docked makes him fly out of the corner; scrolling back reverses it)
      const fr = finaleSlotRef.current?.getBoundingClientRect();
      const finaleY = fr ? fr.top + fr.height / 2 : Infinity;
      // (once you scroll past the finale scene he rides away with it, then jumps into the corner)
      // Footer roughly half in view: he jumps out of the corner into its middle and stays there.
      // Scrolling back out hands him to the finale spot, which is long scrolled past → straight back to the corner
      const footer = footerSlotRef.current?.closest('footer');
      const footerTop = footer ? footer.getBoundingClientRect().top : Infinity;
      if (footerTop <= window.innerHeight * FOOTER_AT) setStage('footer');else if (fr && finaleY <= window.innerHeight * FINALE_AT) setStage('finale');
      // Phones: hand over to the section spot (and start the chat) 15% of the screen height sooner
      else {
        const lead = window.innerWidth < 768 ? window.innerHeight * 0.15 : 0;
        setStage(slotY <= pinnedY + lead ? 'section' : 'hero');
      }
    };
    check();
    window.addEventListener('scroll', check, {
      passive: true
    });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [triggerRef, heroRef, slotRef, finaleSlotRef, footerSlotRef]);

  // Written every frame by the 3D scene — motion values, so no re-renders
  const bubbleX = useMotionValue(-9999);
  const bubbleY = useMotionValue(-9999);
  const floatX = useMotionValue(0);
  const floatY = useMotionValue(0);
  const bubbleLink = useRef<BubbleLink>({
    x: bubbleX,
    y: bubbleY,
    dx: floatX,
    dy: floatY,
    hovered: false
  }).current;

  // Full-charge movie mode
  const cinemaDark = useMotionValue(0);
  const cinemaBars = useMotionValue(0);
  const cinema = useRef<CinemaLink>({
    dark: cinemaDark,
    bars: cinemaBars
  }).current;
  const handleChatPhase = useCallback((phase: ChatPhase, chatEmotion: Emotion) => {
    setChat(c => c.phase === phase && c.emotion === chatEmotion ? c : {
      ...c,
      phase,
      emotion: chatEmotion
    });
  }, []);
  const handleChatPulse = useCallback(() => setChat(c => ({
    ...c,
    pulse: c.pulse + 1
  })), []);
  return <>
      <CinemaOverlay link={cinema} data-id="element-64" data-mp="szenkn33u4kc" />
      <RoboticEyeScene anchorRef={activeAnchor} onDockChange={setDocked} onDockLanded={setDockLanded} onArrive={setArrived} bubbleLink={bubbleLink} onBubblesVisible={setBubblesVisible} onBusyChange={setBusy} cinema={cinema} modelUrl={modelUrl} emotion={emotion} screenScale={screenScale} faceSide={faceSide} armsMode={armsMode} chat={chat} data-id="element-65" data-mp="1pnt9de36mofm" />
      <RobotChat reduce={!!reduce} hostRef={slotRef} active={stage === 'section' && arrived} startDelay={CHAT_DELAY} docked={docked} busy={busy} bubbleLink={bubbleLink} onPhase={handleChatPhase} onPulse={handleChatPulse} data-id="element-66" data-mp="29d4tlzdsb2wm" />
      {/* Finale scene: a different conversation, rendered in the second spot */}
      <RobotChat reduce={!!reduce} hostRef={finaleSlotRef} active={stage === 'finale' && arrived} startDelay={FINALE_CHAT_DELAY} docked={docked} busy={busy} bubbleLink={bubbleLink} onPhase={handleChatPhase} onPulse={handleChatPulse} startNode={FINALE_START} againNode={FINALE_AGAIN} data-id="element-67" data-mp="2ltmp24ak7ps7" />
      <DockChat reduce={!!reduce} landed={docked && dockLanded} onPhase={handleChatPhase} onPulse={handleChatPulse} data-id="element-68" data-mp="34knlolo7j98l" />
    </>;
}